import { FC, memo, useMemo } from 'react'
import { CardProps, Card, Divider, Flex, Box, HStack } from '@chakra-ui/react'

import { Citizen, CitizenFilter } from 'CitizenProfile/types'
import DefaultCitizenCard from './components/DefaultCitizenCard'
import CitizenCardBirth from './components/CitizenCardBirth'
import CitizenDocuments from './components/CitizenCardDocuments'
import CitizenFamily from './components/CitizenCardFamily'
import CitizenCardAddress from './components/CitizenCardAddress'
import CitizenCardPlaces from './components/CitizenCardPlaces'

type Props = CardProps & {
  citizenFilter?: CitizenFilter
  citizen: Citizen
  citizenFields?: string[]
  isDefaultCitizenCard?: boolean
}

const CitizenCard: FC<Props> = ({
  citizenFilter,
  citizen,
  citizenFields = [],
  isDefaultCitizenCard = false,
  children,
  ...rest
}) => {
  const citizenFamilyFields = useMemo(() => {
    return {
      motherName: citizenFields.includes('mother_name'),
      fatherName: citizenFields.includes('father_name'),
    }
  }, [citizenFields])

  const citizenBirthFields = useMemo(() => {
    return {
      birthCity: citizenFields.includes('birth_certificate_city'),
      birthState: citizenFields.includes('birth_certificate_state'),
      birthDate: citizenFields.includes('birth_certificate_date'),
      age: citizenFields.includes('age'),
    }
  }, [citizenFields])

  const citizenPlaceFields = useMemo(() => {
    return {
      healthPlace: citizenFields.includes('health_place'),
      schoolPlace: citizenFields.includes('school_place'),
      socialPlace: citizenFields.includes('social_place'),
      govPlace: citizenFields.includes('gov_place'),
    }
  }, [citizenFields])

  const citizenDocumentsFields = useMemo(() => {
    return {
      cpf: citizenFields.includes('cpf_number'),
      cns: citizenFields.includes('cns_number'),
      pis: citizenFields.includes('ctps_pis_number'),
      rg: citizenFields.includes('rg_number'),
    }
  }, [citizenFields])

  return (
    <Card p={2} height="100%" className="accessible" _hover={{ bg: 'lightPrimary' }} {...rest}>
      {isDefaultCitizenCard ? (
        <HStack flexWrap="wrap" align="center" justify="space-between">
          <DefaultCitizenCard
            citizenFields={citizenFields}
            citizenFilter={citizenFilter}
            citizen={citizen}
            isDefaultCitizenCard={isDefaultCitizenCard}
          />

          <Box>{children}</Box>
        </HStack>
      ) : (
        <Flex direction="column" gap={2}>
          <DefaultCitizenCard citizenFilter={citizenFilter} citizen={citizen} citizenFields={citizenFields} />

          {citizenFields.includes('address') && (
            <>
              <Divider />
              <CitizenCardAddress citizen={citizen} />
            </>
          )}

          {Object.values(citizenPlaceFields).some((booleanValue) => booleanValue) && (
            <>
              <Divider />
              <CitizenCardPlaces citizen={citizen} citizenPlaceFields={citizenPlaceFields} />
            </>
          )}

          {Object.values(citizenFamilyFields).some((booleanValue) => booleanValue) && (
            <>
              <Divider />
              <CitizenFamily
                citizenFilter={citizenFilter}
                citizen={citizen}
                citizenFamilyFields={citizenFamilyFields}
              />
            </>
          )}
          {Object.values(citizenBirthFields).some((booleanValue) => booleanValue) && (
            <>
              <Divider />
              <CitizenCardBirth citizen={citizen} citizenBirthFields={citizenBirthFields} />
            </>
          )}

          {Object.values(citizenDocumentsFields).some((booleanValue) => booleanValue) && (
            <>
              <Divider />
              <CitizenDocuments
                citizenFilter={citizenFilter}
                citizen={citizen}
                citizenDocumentsFields={citizenDocumentsFields}
              />
            </>
          )}
        </Flex>
      )}
    </Card>
  )
}

export default memo(CitizenCard)
